import { Link, useParams } from 'react-router-dom';
import { useComponent } from '../api/hooks';
import { Badge } from '../components/Badge';
import { Card } from '../components/Card';

export default function ComponentDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useComponent(id);

  if (isLoading || !data) return <p className="text-arcforge-500">載入中…</p>;
  if (error) return <p className="text-red-600">錯誤：{(error as Error).message}</p>;

  const m = data.manifest_json;

  return (
    <div className="space-y-4">
      <Link to="/components" className="text-sm text-blue-600 hover:underline">
        ← 元件庫
      </Link>

      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-2xl font-bold text-arcforge-700">{m.id}</h2>
        <Badge tone={m.type}>{m.type}</Badge>
        <span className="text-sm text-arcforge-500">v{m.version}</span>
      </div>

      <p className="text-arcforge-900">{m.description}</p>

      {m.requires && m.requires.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold mb-2">依賴元件</h3>
          <div className="flex flex-wrap gap-2">
            {m.requires.map((r) => (
              <Link key={r} to={`/components/${r}`}>
                <Badge>{r}</Badge>
              </Link>
            ))}
          </div>
        </Card>
      )}

      {m.exposes_hooks && m.exposes_hooks.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold mb-2">
            暴露的 hooks（給 addon 擴充）
          </h3>
          <ul className="space-y-1">
            {m.exposes_hooks.map((h) => (
              <li key={h}>
                <code className="text-xs bg-arcforge-50 px-1.5 py-0.5 rounded">
                  {h}
                </code>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {m.implements_hooks && m.implements_hooks.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold mb-2">實作的 hooks</h3>
          <ul className="space-y-2">
            {m.implements_hooks.map((h: any, idx: number) => (
              <li key={idx} className="text-xs">
                <code className="bg-arcforge-50 px-1.5 py-0.5 rounded">
                  {h.hook}
                </code>
                {h.adds && (
                  <span className="ml-2 text-arcforge-500">
                    adds: {JSON.stringify(h.adds)}
                  </span>
                )}
                {h.ui && (
                  <span className="ml-2 text-arcforge-500">ui: {h.ui}</span>
                )}
                {h.workflow && (
                  <span className="ml-2 text-arcforge-500">
                    workflow: {h.workflow}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {m.provides?.backend_api && m.provides.backend_api.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold mb-2">提供的 API endpoints</h3>
          <ul className="space-y-1">
            {m.provides.backend_api.map((a) => (
              <li key={a}>
                <code className="text-xs bg-arcforge-50 px-1.5 py-0.5 rounded font-mono">
                  {a}
                </code>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card>
        <details>
          <summary className="cursor-pointer text-sm font-semibold select-none">
            完整 manifest
          </summary>
          <pre className="mt-3 text-xs bg-arcforge-50 rounded p-3 overflow-x-auto whitespace-pre">
            {JSON.stringify(m, null, 2)}
          </pre>
        </details>
      </Card>
    </div>
  );
}
